import { useQueryClient } from '@tanstack/react-query';
import { Heart, RefreshCw, Search, X } from 'lucide-react';
import { useEffect, useMemo, useState, useSyncExternalStore } from 'react';
import { useSearchParams } from 'react-router-dom';

import {
  createIntentKey,
  useRequestSynchronizationMutation,
  type TrackCardQuery,
} from '../../api/index.js';
import { useWorkspace } from '../../app/runtime.js';
import { Button } from '../../design-system/button.js';
import { Input } from '../../design-system/form.js';
import { Select } from '../../design-system/select.js';
import { Banner } from '../../design-system/surfaces.js';
import { TrackCollection } from '../tracks/track-collection.js';
import { PageHeader } from '../shared/page.js';

const favoriteFilters = ['any', 'favorite', 'not_favorite'] as const;
const desktopCatalogUtilitiesQuery = '(min-width: 64rem)';

function readDesktopCatalogUtilities() {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return true;
  return window.matchMedia(desktopCatalogUtilitiesQuery).matches;
}

function subscribeToDesktopCatalogUtilities(onChange: () => void) {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return () => {};
  const media = window.matchMedia(desktopCatalogUtilitiesQuery);
  media.addEventListener('change', onChange);
  return () => media.removeEventListener('change', onChange);
}

function useDesktopCatalogUtilities() {
  return useSyncExternalStore(
    subscribeToDesktopCatalogUtilities,
    readDesktopCatalogUtilities,
    () => true,
  );
}
export function buildCatalogTrackQuery({
  connectionId,
  favorite,
  libraryId,
  search,
}: {
  connectionId: string;
  favorite: (typeof favoriteFilters)[number];
  libraryId: string;
  search: string;
}): TrackCardQuery {
  const normalizedSearch = search.trim();
  return {
    connection_id: connectionId,
    library_id: libraryId,
    favorite,
    limit: 50,
    ...(normalizedSearch ? { search: normalizedSearch } : {}),
  };
}

export function CatalogPage() {
  const { connection, library } = useWorkspace();
  const queryClient = useQueryClient();
  const desktopUtilitiesVisible = useDesktopCatalogUtilities();
  const [params, setParams] = useSearchParams();
  const urlSearch = desktopUtilitiesVisible ? (params.get('q') ?? '') : '';
  const [search, setSearch] = useState(urlSearch);
  const favoriteParam = desktopUtilitiesVisible ? params.get('favorite') : null;
  const favorite = favoriteFilters.includes(favoriteParam as (typeof favoriteFilters)[number])
    ? (favoriteParam as (typeof favoriteFilters)[number])
    : 'any';
  const sync = useRequestSynchronizationMutation();
  const [selectionMode, setSelectionMode] = useState(false);

  useEffect(() => setSearch(urlSearch), [urlSearch]);
  useEffect(() => {
    if (desktopUtilitiesVisible) return;
    setSearch('');
    setSelectionMode(false);
    const next = new URLSearchParams(params);
    next.delete('q');
    next.delete('favorite');
    if (next.toString() !== params.toString()) setParams(next, { replace: true });
  }, [desktopUtilitiesVisible, params, setParams]);
  useEffect(() => {
    const timeout = window.setTimeout(() => {
      const next = new URLSearchParams(params);
      if (search.trim()) next.set('q', search.trim());
      else next.delete('q');
      if (next.toString() !== params.toString()) setParams(next, { replace: true });
    }, 250);
    return () => window.clearTimeout(timeout);
  }, [params, search, setParams]);

  const baseQuery = useMemo<TrackCardQuery>(
    () =>
      buildCatalogTrackQuery({
        connectionId: connection.id,
        favorite,
        libraryId: library.id,
        search: urlSearch,
      }),
    [connection.id, favorite, library.id, urlSearch],
  );
  const playbackContext = useMemo(
    () => ({
      kind: 'catalog' as const,
      id: `catalog:${JSON.stringify(baseQuery)}`,
      label: 'Catalog',
    }),
    [baseQuery],
  );

  const setFavoriteFilter = (value: string) => {
    const next = new URLSearchParams(params);
    if (value === 'any') next.delete('favorite');
    else next.set('favorite', value);
    setParams(next, { replace: true });
  };
  const requestSync = async () => {
    await sync.mutateAsync({
      connectionId: connection.id,
      body: { synchronization_kind: 'catalog' },
      idempotencyKey: createIntentKey(),
    });
    await queryClient.invalidateQueries({ queryKey: ['catalog'] });
  };
  const filtering = Boolean(urlSearch || favorite !== 'any');

  return (
    <section>
      <PageHeader
        title="Catalog"
        description={desktopUtilitiesVisible ? 'Tracks from the connected provider account.' : null}
        action={
          desktopUtilitiesVisible ? (
            <Button
              disabled={sync.isPending || connection.status !== 'active'}
              onClick={() => void requestSync()}
              variant="secondary"
            >
              <RefreshCw
                aria-hidden="true"
                className={sync.isPending ? 'animate-spin motion-reduce:animate-none' : undefined}
              />
              {sync.isPending ? 'Requesting…' : 'Sync catalog'}
            </Button>
          ) : null
        }
      />
      {desktopUtilitiesVisible ? (
        <div>
          <div className="mt-6 grid gap-3 md:grid-cols-[minmax(15rem,1fr)_12rem_auto]">
            <label className="relative block">
              <span className="sr-only">Search catalog</span>
              <Search
                aria-hidden="true"
                className="pointer-events-none absolute top-1/2 left-3.5 size-4 -translate-y-1/2 text-ink-3"
              />
              <Input
                className="pl-10"
                maxLength={256}
                onChange={(event) => setSearch(event.currentTarget.value)}
                placeholder="Search catalog…"
                type="search"
                value={search}
              />
            </label>
            <Select
              aria-label="Favorite filter"
              leadingIcon={<Heart aria-hidden="true" className="size-4 text-accent" />}
              onValueChange={setFavoriteFilter}
              options={favoriteFilters.map((value) => ({
                value,
                label:
                  value === 'any' ? 'All tracks' : value === 'favorite' ? 'Liked' : 'Not liked',
              }))}
              value={favorite}
            />
            <Button
              onClick={() => setSelectionMode((current) => !current)}
              variant={selectionMode ? 'ghost' : 'secondary'}
            >
              {selectionMode ? <X aria-hidden="true" /> : null}
              {selectionMode ? 'Cancel' : 'Select'}
            </Button>
          </div>
          {sync.isSuccess ? (
            <Banner className="mt-4" title="Catalog sync requested" tone="success">
              Catalog updates run in the background.
            </Banner>
          ) : null}
          {sync.isError ? (
            <Banner className="mt-4" title="Catalog sync could not be requested" tone="danger">
              Existing catalog content remains available.
            </Banner>
          ) : null}
        </div>
      ) : null}
      <div className={desktopUtilitiesVisible ? 'mt-6' : 'mt-0'}>
        <TrackCollection
          emptyDescription={
            filtering
              ? 'Adjust search or favorite state.'
              : desktopUtilitiesVisible
                ? 'Request a catalog sync to discover tracks from the connected account.'
                : 'Tracks appear here after the connected account is synchronized.'
          }
          emptyTitle={filtering ? 'No tracks match these filters' : 'No catalog tracks yet'}
          errorTitle="Catalog could not be loaded"
          onSelectionModeChange={setSelectionMode}
          playbackContext={playbackContext}
          query={baseQuery}
          selectionMode={selectionMode}
        />
      </div>
    </section>
  );
}
